// This file contains constants used in the environment service, which are meant to be shared cross-application.

import type { AgentSettingsApi } from '@env0/agent-settings-service/api';
import type { BlueprintApi } from '@env0/blueprint-service/api';
import { PlaceholderRepository } from './api.enum';

export const blueprintTypesThatSupportRemoteBackend: BlueprintApi.Blueprint['type'][] = [
  'terraform',
  'workflow',
  'opentofu'
];

export const internalTriggerNames = [
  'cd',
  'prPlan',
  'driftDetection',
  'ttl',
  'scheduled',
  'comment',
  'import',
  'driftRemediation'
] as const;

export const externalTriggerNames = ['user', 'terraform-cli', 'workflow'] as const;

// Share of a usage cap that counts as "approaching the limit". One number for the product: the runs
// banner, the threshold email and the billing screen's meters, chart and upgrade nudge all read it,
// so they cannot disagree on when a warning is due. The product document proposes 75/90/100; 80/100
// is what shipped and what customers are used to, and a 90% band would add a third email a month, so
// changing the set is a product call (ATH-481).
export const RUNS_USAGE_WARNING_THRESHOLD = 0.8;

export const unattendedTriggerNames = ['cd', 'scheduled', 'driftRemediation', 'ttl'] as const;
export type UnattendedTriggerName = (typeof unattendedTriggerNames)[number];

export const AUTOMATIC_DESTROY_TRIGGERS = ['ttl', 'scheduled'] as const;
export type AutomaticDestroyTrigger = (typeof AUTOMATIC_DESTROY_TRIGGERS)[number];

export const DeploymentLogStatuses = [
  'IN_PROGRESS',
  'WAITING_FOR_USER',
  'TIMEOUT',
  'FAILURE',
  'SUCCESS',
  'CANCELLED',
  'INTERNAL_FAILURE',
  'ABORTING',
  'ABORTED',
  'QUEUED',
  'SKIPPED',
  'NEVER_DEPLOYED'
] as const;

export const StoppedByTimeoutReasons = [
  'DEPLOYMENT_TIME_LIMIT_EXCEEDED',
  'AGENT_UNRESPONSIVE',
  'DEPLOYMENT_NOT_STARTED'
] as const;

export const SASS_AGENT_KEY: AgentSettingsApi.SassAgentKey = 'env0-Cloud-agent';

const PLACEHOLDER_REPOSITORIES: string[] = Object.values(PlaceholderRepository);

export const isPlaceholderRepository = (repository: string): boolean => PLACEHOLDER_REPOSITORIES.includes(repository);

export const LAST_MODIFYING_DEPLOYMENT_FIELDS = [
  'startedAt',
  'finishedAt',
  'environmentId',
  'stateFileHash',
  'blueprintType',
  'variables',
  'providerVersions',
  'moduleVersions',
  'gitMetadata'
] as const;
export type LastModifyingDeploymentField = (typeof LAST_MODIFYING_DEPLOYMENT_FIELDS)[number];

// Tag validation limits, shared with the frontend editor so the two charsets cannot drift.
export const MAX_TAG_PAIRS_PER_ENVIRONMENT = 50;
export const MAX_TAG_KEY_LENGTH = 128;
export const MAX_TAG_VALUE_LENGTH = 256;
export const TAG_CHARSET = /^[A-Za-z0-9 _.:/+\-@]+$/;
